import { useState, useRef, useEffect } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Renders a small chat thread with a compose box.
 *
 * Props:
 *   messages    - array of {id, from:"staff"|"supplier", author_name, text, at}
 *   viewerRole  - "staff" | "supplier"  (styles + placeholder text)
 *   onSend      - async (text) => void
 *   isRefetching, isSending
 *   testIdPrefix - namespacing for data-testids
 *   supplierNameField - optional element rendered above the textarea (portal side)
 */
export function ChatPanel({
  messages = [],
  viewerRole,
  onSend,
  isSending = false,
  isRefetching = false,
  testIdPrefix = "chat",
  disabled = false,
  disabledHint,
  supplierNameField,
}) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const submit = async () => {
    if (!draft.trim()) return;
    try {
      await onSend(draft.trim());
      setDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    }
  };

  return (
    <div
      className="border border-border rounded-md bg-white"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <MessageSquare className="size-3.5 text-primary" />
        <p className="overline">
          {viewerRole === "supplier" ? "Questions & clarifications" : "Portal chat"}
        </p>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {isRefetching ? "syncing…" : `${messages.length} msg${messages.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="p-3 space-y-2 max-h-64 overflow-y-auto bg-slate-50/50"
        data-testid={`${testIdPrefix}-thread`}
      >
        {messages.length === 0 ? (
          <p
            className="text-xs text-muted-foreground italic py-4 text-center"
            data-testid={`${testIdPrefix}-empty`}
          >
            {viewerRole === "supplier"
              ? "Ask KINOS anything about this RFQ — replies land here."
              : "No supplier questions yet. Once they type, the thread shows up here."}
          </p>
        ) : (
          messages.map((m) => {
            const mine =
              (viewerRole === "supplier" && m.from === "supplier")
              || (viewerRole === "staff" && m.from === "staff");
            return (
              <div
                key={m.id || `${m.at}-${m.text}`}
                data-testid={`${testIdPrefix}-msg-${m.from}`}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-md px-3 py-2 text-xs ring-1 ring-inset ${
                    m.from === "staff"
                      ? "bg-primary/10 text-primary ring-primary/20"
                      : "bg-white text-foreground ring-slate-200"
                  }`}
                >
                  <p className="font-semibold text-[10px] uppercase tracking-wider opacity-70">
                    {m.author_name || (m.from === "staff" ? "KINOS" : "Supplier")}
                  </p>
                  <p className="whitespace-pre-wrap mt-0.5">{m.text}</p>
                  <p className="text-[9px] opacity-60 mt-1 mono">
                    {timeAgo(m.at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="p-3 border-t border-border space-y-2">
        {supplierNameField}
        <div className="flex items-end gap-2">
          <Textarea
            data-testid={`${testIdPrefix}-input`}
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              viewerRole === "supplier"
                ? "Ask KINOS a question…"
                : "Reply to the supplier…"
            }
            disabled={disabled || isSending}
          />
          <Button
            data-testid={`${testIdPrefix}-send`}
            size="sm"
            onClick={submit}
            disabled={disabled || isSending || !draft.trim()}
            className="bg-primary hover:bg-primary/90 self-stretch"
          >
            {isSending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </div>
        {disabled && disabledHint && (
          <p className="text-[10px] text-muted-foreground">{disabledHint}</p>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export default ChatPanel;
